/**
 * EmployeePermissionsDialog — Enterprise RBAC (Feb 2026).
 *
 * Per-medewerker rechten-detail:
 *   - Toont effective_permissions met bron (Rol / Toestaan / Blokkeren)
 *   - Voor iedere permissie een 3-state widget: Erven / Toestaan / Blokkeren
 *   - Real-time preview van wijzigingen
 *   - Backend enforcement via PUT /admin/rbac/users/{id}/overrides
 */
import React, { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import {
  Loader2, User, Shield, ArrowUpRightFromCircle,
  CheckCircle2, XCircle, Circle,
} from "lucide-react";
import { Button } from "../../ui/button";
import { Badge } from "../../ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "../../ui/dialog";
import { adminApi, formatApiError } from "../../../context/AuthContext";
import PermissionMatrixEditor from "./PermissionMatrixEditor";

const SOURCE_META = {
  role: { label: "Rol", color: "bg-slate-100 text-slate-700" },
  override_allow: { label: "Toegestaan", color: "bg-emerald-100 text-emerald-700" },
  override_deny: { label: "Geblokkeerd", color: "bg-red-100 text-red-700" },
};

export default function EmployeePermissionsDialog({ userId, onClose }) {
  const [effective, setEffective] = useState(null);
  const [overrides, setOverrides] = useState({});   // {perm_key: "allow"|"deny"}
  const [registry, setRegistry] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [rEff, rOv, rReg] = await Promise.all([
        adminApi.get(`/admin/rbac/users/${userId}/effective`),
        adminApi.get(`/admin/rbac/users/${userId}/overrides`),
        adminApi.get("/admin/rbac/permission-registry"),
      ]);
      setEffective(rEff.data);
      const ovs = {};
      (rOv.data.overrides || []).forEach((o) => { ovs[o.permission_key] = o.effect; });
      setOverrides(ovs);
      setRegistry(rReg.data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  }, [userId]);
  useEffect(() => { load(); }, [load]);

  const setOverride = async (perm_key, effect) => {
    try {
      await adminApi.put(`/admin/rbac/users/${userId}/overrides`, {
        permission_key: perm_key, effect,
      });
      // Refresh state
      const next = { ...overrides };
      if (effect === "inherit") delete next[perm_key];
      else next[perm_key] = effect;
      setOverrides(next);
      // Refresh effective
      const rEff = await adminApi.get(`/admin/rbac/users/${userId}/effective`);
      setEffective(rEff.data);
      toast.success(
        effect === "inherit" ? "Terug naar rol-instelling"
        : effect === "allow" ? "Recht toegestaan"
        : "Recht geblokkeerd",
      );
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const currentOverride = (perm_key) => overrides[perm_key] || "inherit";
  const isInEffective = (perm_key) => effective?.effective_permissions?.includes(perm_key);
  const source = (perm_key) => {
    const src = effective?.permission_sources?.[perm_key];
    return SOURCE_META[src] || SOURCE_META.role;
  };

  const renderOverrideControl = (perm) => {
    const cur = currentOverride(perm.key);
    return (
      <div className="flex items-center gap-1.5" data-testid={`override-controls-${perm.key}`}>
        {isInEffective(perm.key) && (
          <Badge className={`${source(perm.key).color} text-[9px] border`} data-testid={`perm-badge-${perm.key}`}>
            {source(perm.key).label}
          </Badge>
        )}
        <div className="inline-flex bg-slate-100 rounded p-0.5 border border-slate-200">
          <button type="button"
                  onClick={() => setOverride(perm.key, "inherit")}
                  className={`px-2 py-0.5 text-[10px] rounded transition-colors ${
                    cur === "inherit"
                      ? "bg-white text-slate-700 shadow-sm font-semibold"
                      : "text-slate-500 hover:text-slate-700"
                  }`}
                  title="Erven van rol"
                  data-testid={`override-inherit-${perm.key}`}>
            Erven
          </button>
          <button type="button"
                  onClick={() => setOverride(perm.key, "allow")}
                  className={`px-2 py-0.5 text-[10px] rounded transition-colors ${
                    cur === "allow"
                      ? "bg-emerald-600 text-white shadow-sm font-semibold"
                      : "text-emerald-600 hover:text-emerald-700"
                  }`}
                  title="Toestaan"
                  data-testid={`override-allow-${perm.key}`}>
            Toestaan
          </button>
          <button type="button"
                  onClick={() => setOverride(perm.key, "deny")}
                  className={`px-2 py-0.5 text-[10px] rounded transition-colors ${
                    cur === "deny"
                      ? "bg-red-600 text-white shadow-sm font-semibold"
                      : "text-red-600 hover:text-red-700"
                  }`}
                  title="Blokkeren"
                  data-testid={`override-deny-${perm.key}`}>
            Blokkeren
          </button>
        </div>
      </div>
    );
  };

  return (
    <Dialog open={true} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-4xl max-h-[92vh] overflow-y-auto"
                     data-testid="employee-permissions-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Shield className="w-5 h-5" /> Rechten bewerken
          </DialogTitle>
          <DialogDescription>
            {loading ? "Rechten laden…" : "Overrides bepalen persoonlijke toegang bovenop de rol."}
          </DialogDescription>
          {!loading && (
            <div className="flex flex-wrap items-center gap-3 pt-1 text-xs text-slate-600">
              <span><User className="w-3 h-3 inline mr-1" /> {effective?.email}</span>
              <span>· Rol: <strong>{effective?.role}</strong></span>
              <span>· <strong>{effective?.effective_permissions?.length || 0}</strong> actieve rechten</span>
              <span>· <strong>{Object.keys(overrides).length}</strong> overrides</span>
            </div>
          )}
        </DialogHeader>

        <div className="space-y-3 py-2">
          <div className="text-[11px] bg-blue-50 border border-blue-200 rounded p-2 text-blue-900">
            <strong>Precedentie:</strong> Overrides gaan boven de rol. <em>Erven</em> = neem de rol-instelling over.
            Wijzigingen worden direct opgeslagen en backend-geënforced (403 bij poging tot niet-toegestaan endpoint).
          </div>

          {loading ? (
            <div className="text-center py-8 text-slate-500 text-sm">
              <Loader2 className="w-4 h-4 inline animate-spin mr-1" /> Rechten laden…
            </div>
          ) : registry && (
            <PermissionMatrixEditor
              registry={registry}
              selectedPermissions={new Set(effective?.effective_permissions || [])}
              onChange={() => {}}    // read-only in override modus
              renderOverrideControl={renderOverrideControl}
            />
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} data-testid="employee-perms-close">Sluiten</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
